import { ReactNode } from "react";
import { observable, ObservableMap } from "mobx";

import { logger } from "@formant/core/common/logger";
import { toUnixTime } from "@formant/date-fns/toUnixTime";
import { duration } from "@formant/core/model/duration";
import { ICommandTemplate } from "@formant/core/model/ICommandTemplate";
import { Timeout } from "@formant/core/model/Timeout";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";

import { IHotkey } from "../component/common/hotkey/IHotkey";
import {
    emptyEventListState,
    IEventListState,
} from "../component/EventList/EventListState";
import { IValueTooltip } from "../model/IValueTooltip";
import { localSettingObservable } from "../utils/localSettingObservable";
import {
    IAppStore,
    IConfirm,
    ISnackbarAction,
    ISnackbarState,
    SnackbarPosition,
} from "./IAppStore";
import { IOperateStore } from "./IOperateStore";

const snackbarDuration = 6 * duration.second;
const snackbarCooldown = 5 * duration.second;

export class AppStore implements IAppStore {
    @observable
    valueTooltip?: IValueTooltip;
    @observable
    error?: Error;
    @observable
    sidebarIsOpen: boolean = false;
    @observable
    sidebarActiveTabIndex: number = 0;
    @localSettingObservable("recentDeviceIds")
    recentDeviceIds: Uuid[] = [];
    @localSettingObservable("favorites")
    favorites: Uuid[] = [];
    @localSettingObservable("utc")
    utc: boolean = false;
    @observable
    allEventsListState = emptyEventListState;
    @observable
    channelEventListState: { [channelId: string]: IEventListState } = {};
    @observable
    deviceEventListState: { [deviceId: string]: IEventListState } = {};
    @observable
    snackbar: ISnackbarState | undefined;
    private snackbarTimeout: Timeout | undefined;
    @observable.ref
    confirm: IConfirm | undefined;
    @observable
    jump: boolean = false;
    @observable
    sharing: boolean = false;
    @observable
    preview: boolean = false;
    @observable.ref
    commandTemplate?: ICommandTemplate;
    private snackbarLastMessageTime: { [id: string]: Timestamp } = {};
    @localSettingObservable("overview.skip-get-started")
    skipGetStarted: boolean = false;
    @localSettingObservable("timeline.scale")
    timelineScale: number = duration.minute;
    @localSettingObservable("mapbox.disabled")
    mapboxDisabled: boolean = false;
    @observable
    teleopLockDevice: boolean = false;

    constructor(private operateStore: IOperateStore) {}

    saveRecentDeviceId(id: Uuid) {
        this.recentDeviceIds = [
            id,
            ...this.recentDeviceIds.filter(_ => _ !== id),
        ].slice(0, 10);
    }

    toggleFavorite(id: Uuid) {
        const toggled = this.favorites.includes(id);
        if (toggled) {
            this.favorites = this.favorites.filter(_ => _ !== id);
        } else {
            this.favorites = [id, ...this.favorites];
        }
    }

    setSnackbar(
        message: ReactNode,
        snackbarAction?: ISnackbarAction,
        position?: SnackbarPosition,
    ): boolean {
        const { share } = this.operateStore;
        const key = message?.toString();
        if (!key) {
            return false;
        }
        const lastMessageTime = this.snackbarLastMessageTime[key];
        const now = new Date().getTime();
        if (lastMessageTime && lastMessageTime + snackbarCooldown > now) {
            return false;
        }
        this.snackbarLastMessageTime[key] = now;
        if (share) {
            return true;
        }

        this.clearSnackbarTimeout();
        const wrappedAction = snackbarAction && {
            ...snackbarAction,
            onClick: () => {
                this.clearSnackbarTimeout();
                this.snackbar = undefined;
                snackbarAction.onClick();
            },
        };
        this.snackbar = {
            message,
            position: position || "side",
            action: wrappedAction,
        };
        this.snackbarTimeout = setTimeout(() => {
            this.snackbar = undefined;
            this.snackbarTimeout = undefined;
        }, snackbarDuration);
        return true;
    }

    clearSnackbar() {
        this.snackbar = undefined;
        this.clearSnackbarTimeout();
    }

    private clearSnackbarTimeout() {
        if (this.snackbarTimeout) {
            clearTimeout(this.snackbarTimeout);
            this.snackbarTimeout = undefined;
        }
    }

    setConfirm(confirm: IConfirm | undefined) {
        this.confirm = confirm;
    }

    onDemandData(streamName: string, start?: Date, end?: Date): void {
        const parameterMeta: Record<string, string> = { streams: streamName };
        if (start && end) {
            parameterMeta.start = toUnixTime(start).toString();
            parameterMeta.end = toUnixTime(end).toString();
        } else if (start) {
            parameterMeta.start = toUnixTime(start).toString();
        }
        this.commandTemplate = {
            command: "formant.demand_data",
            name: "Retrieve on-demand data",
            parameterEnabled: false,
            tags: {},
            parameterMeta,
            deviceFilter: {},
        };
    }

    hotkeys: ObservableMap<string, IHotkey> = observable.map<string, IHotkey>();

    bindHotkey(hotkey: IHotkey) {
        if (this.hotkeys.has(hotkey.code)) {
            logger.warn(
                `Binding hotkey: ${hotkey.code}, ${hotkey.label} already bound; ignoring`,
            );
            return;
        }
        this.hotkeys.set(hotkey.code, hotkey);
    }

    rebindHotkey(hotkey: IHotkey) {
        if (!this.hotkeys.has(hotkey.code)) {
            logger.warn(`Rebinding hotkey: ${hotkey.code} not bound; binding`);
            this.bindHotkey(hotkey);
            return;
        }
        this.hotkeys.set(hotkey.code, hotkey);
    }

    unbindHotkey(code: string) {
        if (!this.hotkeys.has(code)) {
            logger.warn(`Unbinding hotkey: ${code} not bound; ignoring`);
            return;
        }
        this.hotkeys.delete(code);
    }

    customMenuHandlers: Record<string, (() => void)[]> = {};

    addCustomMenuHandler(
        streamName: string,
        menuName: string,
        handler: () => void,
    ) {
        const key = streamName + "_" + menuName;
        let a = this.customMenuHandlers[key];
        if (a === undefined) {
            a = [];
            this.customMenuHandlers[key] = a;
        }
        a.push(handler);
    }

    removeCustomMenuHandler(
        streamName: string,
        menuName: string,
        handler: () => void,
    ) {
        const key = streamName + "_" + menuName;
        let a = this.customMenuHandlers[key];
        if (a) {
            a = a.filter(_ => _ !== handler);
        }
    }

    notifyCustomMenuHandler(streamName: string, menuName: string) {
        const key = streamName + "_" + menuName;
        const a = this.customMenuHandlers[key];
        if (a) {
            a.forEach(_ => _());
        }
    }
}
